// Aggregate ONLY — never import google-reviews.generated.ts (the ~28kB
// review-text array) here; that stays scoped to the testimonials route's
// own lazy chunk (see testimonials-jsonld.ts).
import { GOOGLE_REVIEWS_AGGREGATE } from '../../../reviews/google-reviews-aggregate.generated';
import { BASE_URL } from '../../../sitemap-pages';

export interface FaqQuestion {
  readonly '@type': 'Question';
  readonly name: string;
  readonly acceptedAnswer: {
    readonly '@type': 'Answer';
    readonly text: string;
  };
}

/**
 * The home route's FAQPage questions — the single source of truth for the
 * FAQPage JSON-LD `mainEntity`. `hub-body.component.spec.ts` asserts the
 * visible FAQ answers (hand-authored in hub-body.component.html) equal
 * these `acceptedAnswer.text` values WORD FOR WORD, by importing this same
 * constant — edit the two together.
 */
export const HOME_FAQ_QUESTIONS: readonly FaqQuestion[] = [
  {
    '@type': 'Question',
    name: 'How much do online bass lessons cost?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: '$55/hour, or $50/hour when you buy 4 or more hours in advance. Full details are in Price and scheduling above. For context on how this compares to other private teachers and online platforms, see how much bass lessons cost.',
    },
  },
  {
    '@type': 'Question',
    name: 'What does my setup need to be?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: "You'll need a computer with solid internet, headphones, a mic, and a small audio interface or mixer to get your bass into the call. A headset can stand in for the mic and headphones, but the interface or mixer is not optional. The Zoom setup page walks through what to buy and how it all hooks up.",
    },
  },
  {
    '@type': 'Question',
    name: 'How often do lessons happen?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Weekly, biweekly, or as-needed. Three ways to schedule are above.',
    },
  },
  {
    '@type': 'Question',
    name: 'Can I take a single lesson before committing?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: "Yes. The hourly rate is per-lesson, so there's no package required. Book one, see how it goes, and decide from there. Details are in Price and scheduling above.",
    },
  },
  {
    '@type': 'Question',
    name: "What if I'm a complete beginner?",
    acceptedAnswer: {
      '@type': 'Answer',
      text: "Beginners are welcome. It's a great place to start. We build from the fundamentals: how to hold the bass, right-hand and left-hand technique, reading the fingerboard, and playing in time with simple grooves. See what I teach for how the foundations fit together.",
    },
  },
  {
    '@type': 'Question',
    name: "What if I'm already pretty advanced?",
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'Advanced players are welcome too. I work with a lot of gigging and working pros on improvisation, jazz vocabulary, harmony, reading, and whatever specific area they want to push. Over fifty years of professional playing and over forty teaching means I have material at every level.',
    },
  },
  {
    '@type': 'Question',
    name: 'How long until I see progress?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: "It depends on your goals and how much you practice between lessons. Most students who put in consistent time notice real improvement within a few weeks. I'm not going to promise you'll be gigging in a month, but steady work pays off.",
    },
  },
  {
    '@type': 'Question',
    name: 'Do you teach kids?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: "Yes. I teach kids who are old enough to handle a bass and stay focused for a full lesson. If you're a parent and you're not sure whether your child is ready, give me a call and we can talk it through.",
    },
  },
];

/**
 * Full JSON-LD schema array for `/`: Course (@id /#course) + FAQPage (the
 * eight on-page questions, plain-text answers) + the testimonial
 * VideoObject, in that order after the shell's Org + WebPage (home has no
 * BreadcrumbList — see e2e/data/pages.ts's HOME_PAGE.seo.schemas).
 *
 * Previously lived inline in app.routes.ts's static route data, which is
 * evaluated eagerly at bootstrap — this ~7.3kB block (Course/Offer
 * literals, the full FAQ text, the VideoObject) shipped on every page's
 * initial bundle as a result. Moved here 2026-09-14 (same pass as
 * testimonials/bio/performance/what-i-teach/blog) so it's set at runtime
 * via SEOService — see HomeContentComponent — inside home's own lazy
 * chunk instead.
 */
export function buildHomePageSchemas(): Record<string, unknown>[] {
  return [
    {
      '@type': 'Course',
      '@id': `${BASE_URL}/#course`,
      name: 'Live Online Bass Guitar Lessons',
      description:
        'Live one-on-one bass lessons over Zoom, built around the music you want to play. Teaching professionally since 1985; every lesson is recorded so you keep what we covered.',
      provider: {
        '@type': 'EducationalOrganization',
        name: 'Russ Rodgers Bass Guitar Instruction',
        url: 'https://www.russrodgersbassguitar.com',
      },
      instructor: {
        '@type': 'Person',
        name: 'Russ Rodgers',
        jobTitle: 'Bass Guitar Instructor',
        description:
          'Professional bassist and educator teaching bass guitar since 1985, across rock, funk, jazz, blues, and gospel.',
      },
      audience: {
        '@type': 'Audience',
        audienceType:
          'Bass players of all ages and levels, from first-timers to working professionals',
      },
      offers: [
        {
          '@type': 'Offer',
          name: 'Hourly lesson',
          price: '55.00',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: 55,
            priceCurrency: 'USD',
            unitText: 'per hour',
          },
        },
        {
          '@type': 'Offer',
          name: 'Prepaid rate (4 or more hours)',
          price: '50.00',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: 50,
            priceCurrency: 'USD',
            unitText: 'per hour',
          },
        },
      ],
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'online',
        courseWorkload: 'PT1H',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: GOOGLE_REVIEWS_AGGREGATE.averageRating,
        reviewCount: GOOGLE_REVIEWS_AGGREGATE.count,
        bestRating: 5,
        worstRating: 1,
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: HOME_FAQ_QUESTIONS,
    },
    {
      '@type': 'VideoObject',
      name: 'Online Bass Lessons Testimonial From A Student Of Russ Rodgers Bass Guitar Instruction',
      description:
        'Testimonial from an online Skype bass guitar student of Russ Rodgers on how well remote bass lessons really work.',
      thumbnailUrl: 'https://i.ytimg.com/vi/wrOSCNi5fHI/hqdefault.jpg',
      uploadDate: '2015-09-18T14:09:24-07:00',
      duration: 'PT2M35S',
      contentUrl: 'https://www.youtube.com/watch?v=wrOSCNi5fHI',
      embedUrl: 'https://www.youtube.com/embed/wrOSCNi5fHI',
    },
  ];
}
